import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectCreativeJobsMap } from '@/store/slices/creativeJobs';
import { fetchOwnerWallets } from '@/store/slices/wallet';

import {
  applyLook,
  drawLook,
  fetchSiteRegen,
  NOTHING_TO_RESUME,
  regenerateSiteArt,
  resumeSiteArt,
  revertSiteArt,
  switchSiteArt,
  type ArtStyleResult,
  type SiteRegenState,
} from './art-style-api';
import {
  SITE_REGEN_POLL_MS,
  endedJobToRead,
  isRunningPhase,
  liveSiteRegen,
  otherActiveSiteJobs,
  siteLookOf,
  siteRefusalCopy,
  type SiteAction,
} from './site-regen';

export interface UseSiteRegenOptions {
  tenantId: number | undefined;
  /** Show a (translated) message to the owner. */
  notify: (message: string) => void;
  /** The service refused a charged start because the balance can't cover it (402): open Buy credits, preselecting
   *  what the service says is needed when it says so. */
  onNeedCredits: (needed?: number) => void;
}

export interface UseSiteRegen {
  /** The brand's website look with its job's live progress on top; null until the first read lands, and while the
   *  service has no site jobs here. */
  state: SiteRegenState | null;
  /** The site action whose request is on its way, else null. */
  requesting: SiteAction | null;
  /** For a regeneration asked for on this page: whether it replaces the art players see in place (the site showed the
   *  brand's style when it was asked for). Undefined when that is not known (asked for elsewhere, or before a reload). */
  inPlace: boolean | undefined;
  /** "Regenerate website art" in `styleId` (the brand's active style, saved). */
  regenerate: (styleId: string) => Promise<void>;
  /** "Switch my site". */
  switchSite: () => Promise<void>;
  /** "Back to the original look". */
  revertSite: () => Promise<void>;
  /** "Generate art in this style" for a catalog look: draws the site's art in it without switching. */
  drawLook: (styleId: string) => Promise<void>;
  /** "Set as primary" for a catalog look: switches the site to it. */
  applyLook: (styleId: string) => Promise<void>;
  /** The brand's first read has settled (answered, or failed: `state` then stays null). */
  settled: boolean;
  /** "Retry" of the failed site job where it stopped. False when it left nothing to resume (it failed before
   *  casino-creation took it), so the caller runs its action again. */
  resume: () => Promise<boolean>;
}

type Held = { tenant: number; state: SiteRegenState };

/**
 * The brand's website look on the Art Style page (see `./site-regen`). Read once per brand (`GET …/site-regen`, which
 * a reload reads too), then followed: the job's `creative_job` frames move its phase and parts between reads, its end
 * is read again (that read brings the failed parts and casino-creation's reason), a site job of the brand's that
 * another tab started is picked up, and a running job is re-read every {@link SITE_REGEN_POLL_MS} in case a frame is
 * lost. Reads and action answers can land out of order: the newest one asked for wins. Nothing here can stop a job.
 * A refused action is told to the owner, except a charged start the balance can't cover (402), which goes to
 * `onNeedCredits`; the pre-flight gate on the balance is the caller's (it knows the price and the wallet).
 */
export const useSiteRegen = ({ tenantId, notify, onNeedCredits }: UseSiteRegenOptions): UseSiteRegen => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const jobs = useAppSelector(selectCreativeJobsMap);

  const [held, setHeld] = React.useState<Held | null>(null);
  /** The brand whose first read has settled. */
  const [settledFor, setSettledFor] = React.useState<number | undefined>(undefined);
  const [requesting, setRequesting] = React.useState<SiteAction | null>(null);
  const [inPlaceFor, setInPlaceFor] = React.useState<{ jobId: string; inPlace: boolean } | null>(null);

  const tenantRef = React.useRef(tenantId);
  tenantRef.current = tenantId;
  const notifyRef = React.useRef(notify);
  notifyRef.current = notify;
  const onNeedCreditsRef = React.useRef(onNeedCredits);
  onNeedCreditsRef.current = onNeedCredits;
  const requestingRef = React.useRef<SiteAction | null>(null);
  /** Each read and each action answer takes a ticket; one older than the last adopted is dropped. */
  const ticketRef = React.useRef(0);
  const adoptedRef = React.useRef(0);
  /** Reads already made once, per job: its start seen elsewhere (`<id>:start`) and its end (`<id>:end`). */
  const readForRef = React.useRef<Set<string>>(new Set());
  /** Failed site jobs whose wallet re-read was made (once each). */
  const walletReadForRef = React.useRef<Set<string>>(new Set());

  const adopt = React.useCallback((tenant: number, ticket: number, state: SiteRegenState): void => {
    if (tenantRef.current !== tenant || ticket < adoptedRef.current) return;
    adoptedRef.current = ticket;
    setHeld({ tenant, state });
  }, []);

  const read = React.useCallback(
    async (tenant: number): Promise<void> => {
      ticketRef.current += 1;
      const ticket = ticketRef.current;
      const result = await fetchSiteRegen(tenant);
      if (result.ok) adopt(tenant, ticket, result.value);
      if (tenantRef.current === tenant) setSettledFor(tenant);
    },
    [adopt],
  );

  // Syncs with the service: reads the brand's website look, again on a brand switch.
  React.useEffect(() => {
    readForRef.current.clear();
    if (tenantId === undefined) return;
    void read(tenantId);
  }, [tenantId, read]);

  // Another brand's look is never shown (a late answer for it is dropped too, see `adopt`).
  const state = held && held.tenant === tenantId ? held.state : null;
  const job = state ? jobs[state.job_id] : undefined;
  const view = React.useMemo(() => (state ? liveSiteRegen(state, job) : null), [state, job]);
  const viewRef = React.useRef(view);
  viewRef.current = view;

  // Syncs with the jobs feed: a followed job that ended, or a site job of the brand's that this page does not follow
  // yet (started in another tab), is read once.
  React.useEffect(() => {
    if (tenantId === undefined || !state) return;
    // A job's start and its end are read once each: a job first picked up from the feed (started elsewhere, or its
    // first frame ahead of this page's own answer) still has its end read, which brings its parts and reason.
    const ended = endedJobToRead(state, job);
    const toRead = [
      ...(ended !== null ? [`${ended}:end`] : []),
      ...otherActiveSiteJobs(jobs, tenantId, state.job_id).map((id) => `${id}:start`),
    ].filter((key) => !readForRef.current.has(key));
    if (toRead.length === 0) return;
    for (const key of toRead) readForRef.current.add(key);
    void read(tenantId);
  }, [tenantId, state, job, jobs, read]);

  // Syncs with the wallet: a site job seen failed may have been refunded (its draw's charge), so the balance is read
  // again, once per job, whatever brought the failure (the first read after a return to the page, the poll, a frame):
  // a stale balance would make Retry's start over ask for credits the owner already has back.
  const failedJobId = view?.phase === 'failed' ? view.job_id : '';
  React.useEffect(() => {
    if (!failedJobId || walletReadForRef.current.has(failedJobId)) return;
    walletReadForRef.current.add(failedJobId);
    void dispatch(fetchOwnerWallets());
  }, [failedJobId, dispatch]);

  // Syncs with the service while a job runs: re-reads it on a timer (a frame can be lost; a job runs for hours).
  const running = view !== null && isRunningPhase(view.phase);
  React.useEffect(() => {
    if (!running || tenantId === undefined) return undefined;
    const timer = window.setInterval(() => void read(tenantId), SITE_REGEN_POLL_MS);
    return () => window.clearInterval(timer);
  }, [running, tenantId, read]);

  const runAction = React.useCallback(
    async (
      action: SiteAction,
      send: (tenant: number) => Promise<ArtStyleResult<SiteRegenState>>,
      charged = false,
      quietRefusal?: string,
    ): Promise<string | null> => {
      const tenant = tenantRef.current;
      if (tenant === undefined || requestingRef.current) return null;
      // Whether this regeneration replaces what players see, while the look it starts from is still known.
      const look = siteLookOf(viewRef.current);
      requestingRef.current = action;
      setRequesting(action);
      const result = await send(tenant);
      requestingRef.current = null;
      setRequesting(null);
      // A draw is charged when it is queued: the header chip and the page's balance gate read the wallet again (the
      // owner's, so also after a brand switch).
      if (result.ok && charged) void dispatch(fetchOwnerWallets());
      if (tenantRef.current !== tenant) return null;
      if (result.ok) {
        ticketRef.current += 1;
        adopt(tenant, ticketRef.current, result.value);
        if (action === 'regenerate' && look !== 'unknown') {
          setInPlaceFor({ jobId: result.value.job_id, inPlace: look === 'owner' });
        }
        return null;
      }
      if (quietRefusal !== undefined && result.message === quietRefusal) return result.message;
      // A charged start the balance can't cover: Buy credits is the answer (nothing was queued, nothing was charged).
      if (result.reason === 'insufficient') {
        onNeedCreditsRef.current(result.needed);
        return result.message ?? '';
      }
      // A job already runs (another tab, say): show it.
      if (result.message === 'site_regen_running') void read(tenant);
      notifyRef.current(siteRefusalCopy(result, action, t));
      return result.message ?? '';
    },
    [adopt, read, t, dispatch],
  );

  const regenerate = React.useCallback(
    async (styleId: string): Promise<void> => {
      await runAction('regenerate', (tenant) => regenerateSiteArt(tenant, styleId), true);
    },
    [runAction],
  );
  const switchSite = React.useCallback(async (): Promise<void> => {
    await runAction('switch', switchSiteArt);
  }, [runAction]);
  const revertSite = React.useCallback(async (): Promise<void> => {
    await runAction('revert', revertSiteArt);
  }, [runAction]);
  const drawLookArt = React.useCallback(
    async (styleId: string): Promise<void> => {
      await runAction('look_draw', (tenant) => drawLook(tenant, styleId), true);
    },
    [runAction],
  );
  const applyLookArt = React.useCallback(
    async (styleId: string): Promise<void> => {
      await runAction('look_apply', (tenant) => applyLook(tenant, styleId));
    },
    [runAction],
  );
  const resume = React.useCallback(async (): Promise<boolean> => {
    const action: SiteAction = viewRef.current?.action || 'regenerate';
    // A Retry that resumes is free: no wallet read after it.
    return (await runAction(action, resumeSiteArt, false, NOTHING_TO_RESUME)) !== NOTHING_TO_RESUME;
  }, [runAction]);

  const inPlace = inPlaceFor && view && inPlaceFor.jobId === view.job_id ? inPlaceFor.inPlace : undefined;

  return {
    state: view,
    requesting,
    inPlace,
    regenerate,
    switchSite,
    revertSite,
    drawLook: drawLookArt,
    applyLook: applyLookArt,
    resume,
    settled: tenantId !== undefined && settledFor === tenantId,
  };
};

export default useSiteRegen;
